import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/apiClient";
import { allPages } from "@/lib/pagination";
import TenantSsoSettings from "@/components/TenantSsoSettings";
import CustomerKeySettings from "@/components/CustomerKeySettings";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageBits";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertTriangle, Monitor, ShieldOff } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const { user, refreshMe } = useAuth();
  const [providers, setProviders] = useState([]);
  useEffect(() => { api.get("/auth/sso/providers").then(({ data }) => setProviders(data.providers)).catch(() => {}); }, []);
  const [sessions, setSessions] = useState([]);

  const load = () => allPages("/auth/sessions").then(setSessions).catch(() => {});
  useEffect(() => { load(); }, []);

  const resend = async () => {
    try { await api.post("/auth/resend-verification"); toast.success("Verification email sent"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const revoke = async (id) => {
    try { await api.delete(`/auth/sessions/${id}`); toast.success("Session revoked"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const revokeAll = async () => {
    try { await api.post("/auth/sessions/revoke-all"); toast.success("All sessions revoked"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="settings-page">
      <TenantSsoSettings />
      <CustomerKeySettings />
      {providers.length > 0 && <section className="mb-4 space-y-2 rounded border border-zinc-800 p-4"><h2>Connexion entreprise</h2><p className="text-xs text-zinc-400">Associez votre compte existant à l’identité de votre fournisseur d’entreprise. L’email doit être identique.</p>{providers.map((provider) => <Button variant="outline" key={provider} onClick={async () => { try { const { data } = await api.post(`/auth/sso/${provider}/link`); window.location.assign(data.url); } catch (err) { toast.error(apiError(err)); } }}>Associer {provider.toUpperCase()}</Button>)}</section>}
      <PageHeader title="Settings" description="Account, email verification and active sessions." />

      <div className="space-y-6">
        <div className="rounded-md border border-zinc-800 bg-zinc-900/30 p-5" data-testid="email-verification-card">
          <h2 className="text-sm font-medium text-zinc-200">Email verification</h2>
          <div className="mt-3 flex items-center justify-between">
            {user?.email_verified ? (
              <span className="inline-flex items-center gap-2 text-sm text-emerald-400"><CheckCircle2 className="h-4 w-4" /> {user.email} is verified</span>
            ) : (
              <span className="inline-flex items-center gap-2 text-sm text-amber-400"><AlertTriangle className="h-4 w-4" /> {user?.email} is not verified</span>
            )}
            {!user?.email_verified && (
              <Button data-testid="resend-verification-button" variant="outline" onClick={resend} className="border-zinc-800 bg-zinc-950 hover:bg-zinc-900">Resend email</Button>
            )}
          </div>
        </div>

        <div className="rounded-md border border-zinc-800 bg-zinc-900/30">
          <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-3">
            <h2 className="text-sm font-medium text-zinc-200">Active sessions</h2>
            <Button data-testid="revoke-all-sessions" variant="outline" size="sm" onClick={revokeAll} className="border-red-900/50 bg-zinc-950 text-red-400 hover:bg-red-950/40">
              <ShieldOff className="mr-2 h-3.5 w-3.5" /> Revoke all
            </Button>
          </div>
          <ul className="divide-y divide-zinc-800" data-testid="sessions-list">
            {sessions.map((s) => (
              <li key={s.id} className="flex items-center justify-between px-5 py-3" data-testid={`session-${s.id}`}>
                <div className="flex items-center gap-3">
                  <Monitor className="h-4 w-4 text-zinc-500" />
                  <div>
                    <div className="text-sm text-zinc-200">{s.user_agent?.slice(0, 60) || "Unknown device"} {s.current && <span className="ml-1 rounded bg-emerald-500/10 px-1.5 py-0.5 text-[10px] text-emerald-400">this session</span>}</div>
                    <div className="font-mono text-xs text-zinc-500">{s.ip} · {(s.created_at || "").slice(0, 19).replace("T", " ")}</div>
                  </div>
                </div>
                {!s.current && (
                  <button data-testid={`revoke-session-${s.id}`} onClick={() => revoke(s.id)} className="rounded border border-zinc-800 px-2 py-1 text-xs text-zinc-400 hover:bg-zinc-800">Revoke</button>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
